import * as React from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import {
  CircleAlertIcon,
  CircleCheckIcon,
  InfoIcon,
  TriangleAlertIcon,
  XIcon,
} from "@/components/icons";

// MUI's Alert, which the pane uses ~50 times: a severity, an icon, a message, and an
// optional close button or action. `standard` is the tinted callout; `filled` is the
// solid one a snackbar carries.

export type AlertSeverity = "error" | "warning" | "info" | "success";

const alertVariants = cva(
  "flex items-start gap-2 rounded-lg px-3 py-1.5 text-[12.5px] leading-[1.5] [&>[data-slot=alert-icon]]:mt-[3px] [&>[data-slot=alert-icon]]:size-4",
  {
    variants: {
      variant: { standard: "", filled: "" },
      severity: { error: "", warning: "", info: "", success: "" },
    },
    compoundVariants: [
      {
        variant: "standard",
        severity: "error",
        className:
          "bg-error-tint text-error-dark dark:text-error-light [&>[data-slot=alert-icon]]:text-error",
      },
      {
        variant: "standard",
        severity: "warning",
        className:
          "bg-warning-tint text-warning-dark dark:text-warning-light [&>[data-slot=alert-icon]]:text-warning",
      },
      {
        variant: "standard",
        severity: "info",
        className:
          "bg-primary-tint text-primary-dark dark:text-primary-light [&>[data-slot=alert-icon]]:text-primary",
      },
      {
        variant: "standard",
        severity: "success",
        className:
          "bg-success-tint text-success-dark dark:text-success-light [&>[data-slot=alert-icon]]:text-success",
      },
      { variant: "filled", severity: "error", className: "bg-error text-status-foreground" },
      { variant: "filled", severity: "warning", className: "bg-warning text-status-foreground" },
      { variant: "filled", severity: "info", className: "bg-primary text-primary-foreground" },
      { variant: "filled", severity: "success", className: "bg-success text-status-foreground" },
    ],
    defaultVariants: { variant: "standard", severity: "info" },
  }
);

const ICONS: Record<
  AlertSeverity,
  React.ComponentType<{ className?: string; "data-slot"?: string }>
> = {
  error: CircleAlertIcon,
  warning: TriangleAlertIcon,
  info: InfoIcon,
  success: CircleCheckIcon,
};

interface AlertProps extends Omit<React.ComponentProps<"div">, "title"> {
  severity?: AlertSeverity;
  variant?: "standard" | "filled";
  /** A bold first line, MUI's AlertTitle. */
  title?: React.ReactNode;
  /** Shows a close button that calls this. */
  onClose?: () => void;
  /** A button at the end of the row, instead of or beside the close button. */
  action?: React.ReactNode;
  /** `false` for no icon. */
  icon?: React.ReactNode | false;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      className,
      severity = "info",
      variant = "standard",
      title,
      onClose,
      action,
      icon,
      children,
      ...props
    },
    ref
  ) => {
    const Icon = ICONS[severity];
    return (
      <div
        ref={ref}
        role="alert"
        data-slot="alert"
        data-severity={severity}
        className={cn(alertVariants({ variant, severity }), className)}
        {...props}
      >
        {icon === false ? null : icon !== undefined ? (
          <span data-slot="alert-icon" className="flex shrink-0">
            {icon}
          </span>
        ) : (
          <Icon data-slot="alert-icon" className="shrink-0" />
        )}
        <div className="min-w-0 flex-1 py-px">
          {title && <div className="font-semibold">{title}</div>}
          {children}
        </div>
        {action && <div className="-my-0.5 flex shrink-0 items-center">{action}</div>}
        {onClose && (
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="-mr-1 mt-px flex size-6 shrink-0 items-center justify-center rounded-md opacity-70 hover:bg-accent hover:opacity-100"
          >
            <XIcon className="size-4" />
          </button>
        )}
      </div>
    );
  }
);
Alert.displayName = "Alert";

export { Alert };
export type { AlertProps };
